/**
 * Permanent proxy URLs for email signature assets.
 *
 * v2.16.38: email signatures (compose + campaigns) used to bake in 1h
 * S3 presigned URLs for {{SenderPhoto}} / {{CompanyLogo}}. Recipients
 * opening the email more than an hour later saw a broken image. These
 * helpers emit a permanent backend proxy URL instead — the proxy
 * regenerates a fresh presigned URL on every request, so the bytes
 * stay reachable for as long as the underlying S3 object exists.
 *
 * Keep this file in sync with backend/routes/signatures.py
 * (_signature_photo_url / _signature_logo_url) — both paths must agree
 * so it doesn't matter whether the merge vars are resolved server- or
 * client-side.
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL || "https://api.nurturecrm.ai";

const isExternalUrl = (v) =>
    typeof v === "string" && (v.startsWith("http://") || v.startsWith("https://"));

/** Proxy URL for the current user's profile photo, or "" if none. */
export function senderPhotoUrl(user) {
    if (!user?.user_id) return "";
    const hasS3 = !!user.picture_s3_key;
    if (hasS3 || isExternalUrl(user.picture)) {
        return `${API_BASE}/api/public/user-photo/${user.user_id}`;
    }
    return "";
}

/** Proxy URL for the current user's company logo. */
export function companyLogoUrl(user) {
    if (!user?.user_id) return "";
    const hasS3 = !!user.company_logo_s3_key;
    if (hasS3 || isExternalUrl(user.company_logo_url)) {
        return `${API_BASE}/api/public/user-logo/${user.user_id}`;
    }
    return "";
}

/** Proxy URL for the current user's org logo (tenant-level branding). */
export function orgLogoUrl(user) {
    const orgId = user?.org_id;
    if (!orgId) return "";
    // org_logo_url on /me is a presigned URL; org_logo_s3_key isn't exposed
    // to the frontend, so we fall back on the presence of org_logo_url
    // (which implies the backend has either an s3_key or a direct URL).
    if (isExternalUrl(user.org_logo_url)) {
        return `${API_BASE}/api/public/org-logo/${orgId}`;
    }
    return "";
}

/**
 * Merge-map key/value pair for {{SenderPhoto}} + {{CompanyLogo}} +
 * {{OrgLogo}}. Most callers merge these into a larger merge_map for
 * signature resolution; this helper keeps the five resolution points
 * (Inbox, EmailEditorModal, SequenceEditor, SignatureEditor) in sync.
 */
export function signatureAssetMergeMap(user) {
    return {
        "{{SenderPhoto}}": senderPhotoUrl(user),
        "{{CompanyLogo}}": companyLogoUrl(user) || orgLogoUrl(user),
    };
}
